/*
 * La cicogna — Logica del form "Desidero la fattura" nel carrello.
 *
 * Sostituisce lo <script> inline che stava in layout/theme.liquid.
 * Markup: snippets/invoice-form.liquid (reso da sections/main-cart.liquid e
 * snippets/header-actions.liquid, solo per l'Italia).
 *
 * PRINCIPIO DI FONDO — perché è scritto così:
 * la versione precedente scriveva gli attributi sul carrello ad ogni battuta di
 * tasto, senza validare, e bloccava il checkout solo con `button.disabled`.
 * Ne uscivano ordini con "Richiesta Fattura: Sì" e nessun Codice Fiscale, perché
 * Shopify elimina gli attributi con valore stringa vuota e perché `disabled`
 * viene cancellato dal morph del carrello (assets/morph.js → copyAttributes).
 *
 * Qui invece:
 *   1. il carrello riceve gli attributi SOLO se la richiesta è completa e valida;
 *      in ogni altro caso li AZZERA. Lo stato intermedio è irrappresentabile,
 *      quindi non può nascere un ordine a metà nemmeno aggirando la UI.
 *   2. prima di lasciare la pagina verso il checkout la scrittura viene attesa
 *      (flush sincrono), così l'ultimo valore digitato non si perde più.
 *   3. il blocco vive su un listener in capture su `document`: il morph non può
 *      rimuoverlo, a differenza di un attributo sul bottone.
 */

import { fetchConfig } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

const STORAGE_KEY = 'cicogna_invoice_draft_v11';
const DEBOUNCE_MS = 400;
const DEBUG = false; /* La cicogna: impostare true per abilitare i log di debug */

/** Nomi esatti degli attributi carrello gestiti da questo modulo. */
const ATTR = {
  request: 'Richiesta Fattura',
  type: 'Tipo Cliente',
  cf: 'Codice Fiscale',
  company: 'Ragione Sociale',
  vat: 'Partita IVA',
  einvoice: 'PEC o SDI',
};

/**
 * Payload che rimuove ogni attributo fattura dal carrello.
 * Shopify cancella un attributo quando riceve valore stringa vuota.
 */
const CLEARED = Object.freeze({
  [ATTR.request]: '',
  [ATTR.type]: '',
  [ATTR.cf]: '',
  [ATTR.company]: '',
  [ATTR.vat]: '',
  [ATTR.einvoice]: '',
});

const Validators = {
  cf: (value) => /^[A-Z0-9]{16}$/i.test(value),
  piva: (value) => /^[0-9]{11}$/.test(value),
  pecOrSdi: (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) || /^[A-Z0-9]{6,7}$/i.test(value),
  notEmpty: (value) => value.trim().length > 0,
};

function log(message, data) {
  if (!DEBUG) return;
  console.log(`%c[La cicogna — fattura] ${message}`, 'color: #f4b911; font-weight: bold;', data ?? '');
}

/* ------------------------------------------------------------------ *
 * Lettura dello stato dal DOM
 * ------------------------------------------------------------------ */

/**
 * Il form può esistere in due punti (pagina carrello e drawer), mai
 * contemporaneamente visibili. Si prende quello effettivamente visibile.
 * @returns {HTMLElement | null}
 */
function getWrapper() {
  const wrappers = /** @type {HTMLElement[]} */ (Array.from(document.querySelectorAll('[data-invoice-wrapper]')));
  if (wrappers.length === 0) return null;
  return wrappers.find((wrapper) => wrapper.offsetParent !== null) ?? wrappers[0];
}

/**
 * @param {HTMLElement} wrapper
 */
function getFields(wrapper) {
  /** @param {string} selector */
  const find = (selector) => /** @type {HTMLInputElement | HTMLSelectElement | null} */ (wrapper.querySelector(selector));

  return {
    checkbox: /** @type {HTMLInputElement | null} */ (find('[data-invoice-request]')),
    type: /** @type {HTMLSelectElement | null} */ (find('[data-invoice-type]')),
    cf: find('[data-invoice-field="cf"]'),
    company: find('[data-invoice-field="company"]'),
    vat: find('[data-invoice-field="vat"]'),
    einvoice: find('[data-invoice-field="einvoice"]'),
  };
}

/**
 * @returns {{
 *   wrapper: HTMLElement,
 *   fields: ReturnType<typeof getFields>,
 *   checked: boolean,
 *   type: 'Privato' | 'Azienda',
 *   cf: string, company: string, vat: string, einvoice: string,
 * } | null}
 */
function readState() {
  const wrapper = getWrapper();
  if (!wrapper) return null;

  const fields = getFields(wrapper);
  if (!fields.checkbox) return null;

  return {
    wrapper,
    fields,
    checked: fields.checkbox.checked,
    type: fields.type?.value === 'Azienda' ? 'Azienda' : 'Privato',
    // Codice fiscale e partita IVA vanno normalizzati: l'utente li scrive spesso in minuscolo.
    cf: (fields.cf?.value ?? '').trim().toUpperCase(),
    company: (fields.company?.value ?? '').trim(),
    vat: (fields.vat?.value ?? '').trim(),
    einvoice: (fields.einvoice?.value ?? '').trim(),
  };
}

/**
 * Valida solo i campi del "Tipo Cliente" selezionato.
 * @param {NonNullable<ReturnType<typeof readState>>} state
 */
function validate(state) {
  if (!state.checked) return { valid: true, invalidField: null };

  if (state.type === 'Privato') {
    if (!Validators.cf(state.cf)) return { valid: false, invalidField: state.fields.cf };
    return { valid: true, invalidField: null };
  }

  if (!Validators.notEmpty(state.company)) return { valid: false, invalidField: state.fields.company };
  if (!Validators.piva(state.vat)) return { valid: false, invalidField: state.fields.vat };
  if (!Validators.pecOrSdi(state.einvoice)) return { valid: false, invalidField: state.fields.einvoice };
  return { valid: true, invalidField: null };
}

/**
 * Riporta nel DOM i valori normalizzati (trim + uppercase).
 * Serve prima di un re-invio nativo del form: `pattern` e `required` validano il
 * valore così com'è nell'input, quindi uno spazio di troppo bloccherebbe
 * `requestSubmit()` in silenzio pur avendo superato la nostra validazione.
 *
 * @param {NonNullable<ReturnType<typeof readState>>} state
 */
function normalizeInputs(state) {
  const { fields } = state;
  if (fields.cf) fields.cf.value = state.cf;
  if (fields.company) fields.company.value = state.company;
  if (fields.vat) fields.vat.value = state.vat;
  if (fields.einvoice) fields.einvoice.value = state.einvoice;
}

/**
 * Il cuore del fix: o niente richiesta, o una richiesta completa.
 * I campi del ramo non selezionato vengono sempre azzerati, così chi compila
 * i dati Azienda e poi torna su Privato non porta entrambi sull'ordine.
 *
 * @param {NonNullable<ReturnType<typeof readState>>} state
 * @param {boolean} isValid
 */
function buildPayload(state, isValid) {
  if (!state.checked || !isValid) return { ...CLEARED };

  if (state.type === 'Privato') {
    return { ...CLEARED, [ATTR.request]: 'Sì', [ATTR.type]: 'Privato', [ATTR.cf]: state.cf };
  }

  return {
    ...CLEARED,
    [ATTR.request]: 'Sì',
    [ATTR.type]: 'Azienda',
    [ATTR.company]: state.company,
    [ATTR.vat]: state.vat,
    [ATTR.einvoice]: state.einvoice,
  };
}

/* ------------------------------------------------------------------ *
 * Scrittura sul carrello — serializzata e flushabile
 * ------------------------------------------------------------------ */

/** @type {Record<string, string> | null} */
let queued = null;
/** @type {ReturnType<typeof setTimeout> | null} */
let debounceTimer = null;
/** @type {Promise<unknown>} */
let inFlight = Promise.resolve();
/** Ultimo payload effettivamente inviato, per non ripetere scritture identiche. */
let lastSent = '';

/** @param {Record<string, string>} payload */
function postAttributes(payload) {
  return fetch(Theme.routes.cart_update_url, fetchConfig('json', { body: JSON.stringify({ attributes: payload }) }));
}

/** @param {Record<string, string>} payload */
function scheduleSync(payload) {
  // Finché la richiesta è incompleta il payload resta "tutto vuoto": senza questo
  // controllo ogni battuta di tasto rimanderebbe la stessa scrittura.
  if (JSON.stringify(payload) === lastSent) return;

  queued = payload;
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    flushSync();
  }, DEBOUNCE_MS);
}

/**
 * Invia subito quanto in attesa e restituisce una promise che si risolve a
 * scrittura conclusa. È questo che elimina la race con la navigazione al checkout.
 */
function flushSync() {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }

  if (queued) {
    const payload = queued;
    queued = null;
    lastSent = JSON.stringify(payload);
    log('scrittura attributi sul carrello', payload);
    inFlight = inFlight
      .then(() => postAttributes(payload))
      .catch((error) => {
        // Se la scrittura fallisce non si può considerarla inviata.
        lastSent = '';
        log('errore di scrittura', error);
      });
  }

  return inFlight;
}

/* ------------------------------------------------------------------ *
 * Bozza locale (solo per i valori non ancora committati)
 * ------------------------------------------------------------------ */

/** @param {NonNullable<ReturnType<typeof readState>>} state */
function saveDraft(state) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        checked: state.checked,
        type: state.type,
        cf: state.cf,
        company: state.company,
        vat: state.vat,
        einvoice: state.einvoice,
      })
    );
  } catch (error) {
    log('localStorage non disponibile', error);
  }
}

function readDraft() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
}

/**
 * Reidrata il form con la bozza locale. Serve dopo un morph del carrello, che
 * riporta gli input ai valori resi dal server (assets/morph.js → updateInput).
 * Il server è comunque autorevole: la bozza riempie solo i campi rimasti vuoti.
 */
function restoreDraft() {
  const state = readState();
  if (!state) return;

  const draft = readDraft();
  if (!draft) return;

  const { fields } = state;
  if (draft.checked && fields.checkbox) fields.checkbox.checked = true;
  if (draft.type && fields.type) fields.type.value = draft.type;

  /** @param {HTMLInputElement | HTMLSelectElement | null} input @param {string} value */
  const fill = (input, value) => {
    if (input && !input.value && value) input.value = value;
  };

  fill(fields.cf, draft.cf);
  fill(fields.company, draft.company);
  fill(fields.vat, draft.vat);
  fill(fields.einvoice, draft.einvoice);
}

/* ------------------------------------------------------------------ *
 * UI
 * ------------------------------------------------------------------ */

/**
 * Lo stato visivo passa da data-attribute + CSS (vedi assets/base.css), non da
 * stili inline: sono più leggibili e vengono comunque ri-applicati ad ogni
 * `cart:update`, dato che il morph li azzera.
 *
 * @param {NonNullable<ReturnType<typeof readState>>} state
 * @param {boolean} isValid
 * @param {boolean} showError
 */
function applyUi(state, isValid, showError) {
  const { wrapper, fields } = state;

  wrapper.dataset.invoiceOpen = String(state.checked);
  wrapper.dataset.invoiceType = state.type;
  wrapper.dataset.invoiceError = String(showError && !isValid);

  /*
   * Solo i campi del ramo attivo sono abilitati e obbligatori. `disabled` fa due
   * cose insieme: li esclude dal POST nativo del form carrello (così il ramo non
   * scelto non finisce sull'ordine) e li esenta dalla validazione nativa, che
   * altrimenti bloccherebbe il submit su un campo dentro un display:none — dove
   * non può nemmeno ricevere il focus. I valori restano comunque digitati, quindi
   * cambiando Tipo Cliente non si perde nulla.
   */
  const wantsPrivate = state.checked && state.type === 'Privato';
  const wantsCompany = state.checked && state.type === 'Azienda';

  /**
   * @param {HTMLInputElement | HTMLSelectElement | null} input
   * @param {boolean} active
   */
  const setActive = (input, active) => {
    if (!input) return;
    input.disabled = !active;
    if (input instanceof HTMLInputElement) input.required = active;
  };

  setActive(fields.type, state.checked);
  setActive(fields.cf, wantsPrivate);
  setActive(fields.company, wantsCompany);
  setActive(fields.vat, wantsCompany);
  setActive(fields.einvoice, wantsCompany);

  /*
   * Segnalazione visiva del checkout non disponibile. Di proposito NON si usa
   * `button.disabled`: era la vecchia soluzione, e oltre a venire cancellata dal
   * morph rendeva il bottone inerte, senza spiegare il perché. Lasciandolo
   * cliccabile è il gate sul submit a mostrare l'errore e a portare il focus sul
   * campo mancante. `disabled` resta libero per l'uso che ne fa il tema (carrello vuoto).
   */
  const blocked = state.checked && !isValid;
  for (const button of document.querySelectorAll('button[name="checkout"], input[name="checkout"]')) {
    button.classList.toggle('cicogna-checkout-blocked', blocked);
    if (blocked) button.setAttribute('aria-disabled', 'true');
    else button.removeAttribute('aria-disabled');
  }
}

/* ------------------------------------------------------------------ *
 * Ciclo principale
 * ------------------------------------------------------------------ */

/**
 * @param {object} [options]
 * @param {boolean} [options.write] - se true, programma la scrittura sul carrello
 * @param {boolean} [options.showError]
 */
function sync({ write = false, showError = false } = {}) {
  const state = readState();
  if (!state) return null;

  const { valid } = validate(state);
  applyUi(state, valid, showError);
  saveDraft(state);

  if (write) scheduleSync(buildPayload(state, valid));

  return state;
}

/**
 * Auto-riparazione: se il carrello porta ancora attributi fattura (per esempio
 * lasciati dalla versione precedente di questo script) ma lo stato attuale non è
 * una richiesta valida, li si rimuove subito.
 */
function reconcileWithCart() {
  const state = readState();
  if (!state) return;

  const serverHasRequest = state.wrapper.dataset.invoiceServerRequest === 'true';
  if (!serverHasRequest) return;

  const { valid } = validate(state);
  if (state.checked && valid) return;

  log('carrello con dati fattura incompleti: pulizia');
  scheduleSync({ ...CLEARED });
  flushSync();
}

/* ------------------------------------------------------------------ *
 * Listener
 * ------------------------------------------------------------------ */

document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;
  if (!target.matches('[data-invoice-request], [data-invoice-type]')) return;
  sync({ write: true });
});

document.addEventListener('input', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;
  if (!target.matches('[data-invoice-field]')) return;
  sync({ write: true });
});

/*
 * Ri-applica lo stato dopo ogni aggiornamento del carrello: `morphSection`
 * cancella attributi e valori degli input riportandoli all'HTML del server.
 */
document.addEventListener(ThemeEvents.cartUpdate, () => {
  restoreDraft();
  sync();
});

/* ------------------------------------------------------------------ *
 * Gate sul checkout — l'unico blocco che conta
 * ------------------------------------------------------------------ */

/** Evita il rientro quando siamo noi a ri-inviare il form. */
let bypassGate = false;

/** @param {EventTarget | null} target */
function isCartForm(target) {
  return target instanceof HTMLFormElement && (target.id === 'cart-form' || target.classList.contains('cart-form'));
}

document.addEventListener(
  'submit',
  (event) => {
    if (bypassGate) {
      bypassGate = false;
      return;
    }

    if (!isCartForm(event.target)) return;

    const form = /** @type {HTMLFormElement} */ (event.target);
    const submitter = /** @type {HTMLElement | null} */ (
      /** @type {SubmitEvent} */ (event).submitter
    );

    // Interessa solo il percorso verso il checkout, non gli altri submit del form carrello.
    if (submitter instanceof HTMLButtonElement || submitter instanceof HTMLInputElement) {
      if (submitter.name && submitter.name !== 'checkout') return;
    }

    const state = readState();
    if (!state || !state.checked) return;

    const { valid, invalidField } = validate(state);

    if (!valid) {
      event.preventDefault();
      event.stopImmediatePropagation();
      applyUi(state, false, true);
      if (invalidField) {
        invalidField.focus();
        invalidField.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
      log('checkout bloccato: dati fattura incompleti');
      return;
    }

    // Dati validi: gli attributi devono essere sul carrello PRIMA di lasciare la
    // pagina, altrimenti la scrittura in attesa muore con l'unload.
    event.preventDefault();
    event.stopImmediatePropagation();

    normalizeInputs(state);
    scheduleSync(buildPayload(state, true));
    flushSync().then(() => {
      log('attributi salvati, procedo al checkout');
      bypassGate = true;
      try {
        form.requestSubmit(
          submitter instanceof HTMLButtonElement || submitter instanceof HTMLInputElement ? submitter : undefined
        );
      } catch (error) {
        // Se il re-invio non parte il gate resterebbe aperto per il submit successivo.
        bypassGate = false;
        log('requestSubmit non riuscito, vado al checkout direttamente', error);
        // Shopify.routes.root include l'eventuale prefisso di lingua/mercato.
        window.location.href = `${window.Shopify.routes.root}checkout`;
      }
    });
  },
  true
);

/*
 * Stessa protezione per un eventuale link diretto al checkout: il gate sul submit
 * non lo intercetterebbe.
 */
document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const link = target.closest('a[href*="/checkout"]');
    if (!link) return;

    const state = readState();
    if (!state || !state.checked) return;

    const { valid, invalidField } = validate(state);
    if (!valid) {
      event.preventDefault();
      applyUi(state, false, true);
      invalidField?.focus();
      return;
    }

    event.preventDefault();
    scheduleSync(buildPayload(state, true));
    flushSync().then(() => {
      window.location.href = /** @type {HTMLAnchorElement} */ (link).href;
    });
  },
  true
);

/* ------------------------------------------------------------------ *
 * Init
 * ------------------------------------------------------------------ */

/** Wrapper già agganciati, per non ri-inizializzarli ad ogni mutazione del DOM. */
const initialised = new WeakSet();

function init() {
  const wrapper = getWrapper();
  if (wrapper) initialised.add(wrapper);

  restoreDraft();
  sync();
  reconcileWithCart();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

/*
 * Il form del drawer viene inserito nel DOM al primo render della sezione.
 * L'observer serve solo ad agganciare un wrapper mai visto prima: senza il
 * WeakSet girerebbe ad ogni singola mutazione della pagina.
 */
const observer = new MutationObserver(() => {
  const wrapper = getWrapper();
  if (!wrapper || initialised.has(wrapper)) return;

  initialised.add(wrapper);
  restoreDraft();
  sync();
});
observer.observe(document.body, { childList: true, subtree: true });
